import React from 'react';

/**
 * Full page loading spinner - shown while a page's code, or which modules are on, is still on its way. Tagged
 * data-page-loading: the refusing-server guard waits while one is on screen, so a page's own loads are counted on it.
 */
export const PageLoader = ({ message = 'Loading...' }) => (
    <div className="flex flex-col items-center justify-center min-h-[400px]" data-page-loading>
        <span className="loading loading-spinner loading-lg text-primary"></span>
        <p className="mt-4 text-base-content/70">{message}</p>
    </div>
);

/**
 * Inline loading spinner for buttons and small areas
 */
export const InlineLoader = ({ size = 'md', className = '' }) => {
    const sizeClasses = {
        xs: 'loading-xs',
        sm: 'loading-sm',
        md: 'loading-md',
        lg: 'loading-lg'
    };
    
    return (
        <span className={`loading loading-spinner ${sizeClasses[size]} ${className}`}></span>
    );
};

/**
 * Skeleton loader for card-like content
 */
export const CardSkeleton = ({ count = 1 }) => (
    <>
        {Array.from({ length: count }).map((_, index) => (
            <div key={index} className="card bg-base-100 shadow-xl animate-pulse">
                <div className="card-body">
                    <div className="h-4 bg-base-300 rounded w-3/4 mb-4"></div>
                    <div className="h-3 bg-base-300 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-base-300 rounded w-2/3"></div>
                </div>
            </div>
        ))}
    </>
);

/**
 * Skeleton loader for table rows
 */
export const TableRowSkeleton = ({ columns = 4, rows = 5 }) => (
    <>
        {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex} className="animate-pulse">
                {Array.from({ length: columns }).map((_, colIndex) => (
                    <td key={colIndex}>
                        <div className="h-4 bg-base-300 rounded w-full"></div>
                    </td>
                ))}
            </tr>
        ))}
    </>
);

/**
 * Skeleton loader for list items
 */
export const ListSkeleton = ({ count = 5 }) => (
    <div className="space-y-3">
        {Array.from({ length: count }).map((_, index) => (
            <div key={index} className="flex items-center space-x-4 animate-pulse">
                <div className="w-10 h-10 bg-base-300 rounded-full"></div>
                <div className="flex-1">
                    <div className="h-4 bg-base-300 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-base-300 rounded w-1/2"></div>
                </div>
            </div>
        ))}
    </div>
);

/**
 * Overlay loader for async operations
 */
export const OverlayLoader = ({ message = 'Please wait...' }) => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-base-100 rounded-lg p-6 flex flex-col items-center">
            <span className="loading loading-spinner loading-lg text-primary"></span>
            <p className="mt-4 text-base-content">{message}</p>
        </div>
    </div>
);

/**
 * Button with loading state
 */
export const LoadingButton = ({ 
    loading, 
    children, 
    loadingText = 'Loading...', 
    className = '', 
    ...props 
}) => (
    <button 
        className={`btn ${className}`} 
        disabled={loading || props.disabled}
        {...props}
    >
        {loading ? (
            <>
                <InlineLoader size="sm" />
                {loadingText}
            </>
        ) : children}
    </button>
);

export default PageLoader;
